/**
 * Filter Actions
 *
 * This module provides actions for filtering snippets:
 * 1. Actions for setting filter state (category, search query)
 * 2. An action (getFilteredSnippets) that computes filtered snippets based on current filter state
 *
 * The getFilteredSnippets action computes the filtered list on-demand rather than storing
 * filtered results in state, which prevents stale data issues and keeps the store simpler.
 *
 * Filter precedence:
 * 1. Category filter is applied first (MY_TEMPLATES, FAVORITES, or ALL)
 * 2. Search query is then applied to the category-filtered results
 *
 * Search matches against: snippet name, owner display name, and description
 */
import type { StoreActionApi } from 'react-sweet-state';

import type { Snippet } from '../../snippetTypes.ts';
import { DEFAULT_CATEGORY_ID, SNIPPET_CATEGORY_IDS } from '../../constants/snippetCategories.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

/**
 * Pure function to filter snippets based on category and search query.
 * Extracted for testability and reuse.
 */
export const filterSnippets = (
	snippets: Snippet[],
	categoryId: string,
	searchQuery: string,
	currentUserId?: string | null,
): Snippet[] => {
	let categoryFiltered = snippets;

	// Apply category filter
	if (categoryId === SNIPPET_CATEGORY_IDS.MY_TEMPLATES) {
		categoryFiltered = snippets.filter((snippet) => {
			if (currentUserId) {
				return snippet.createdBy.accountId.toLowerCase() === currentUserId.toLowerCase();
			}
			return false;
		});
	} else if (categoryId === SNIPPET_CATEGORY_IDS.FAVORITES) {
		categoryFiltered = snippets.filter((snippet) => snippet?.isFavorite);
	}

	// Apply search filter
	const trimmedSearchQuery = searchQuery.trim().toLowerCase();
	if (!trimmedSearchQuery) {
		return categoryFiltered;
	}

	return categoryFiltered.filter((snippet) => {
		const nameMatch = snippet.name.toLowerCase().includes(trimmedSearchQuery);
		const ownerMatch = snippet.createdBy.displayName.toLowerCase().includes(trimmedSearchQuery);
		const descriptionMatch = snippet.description?.toLowerCase().includes(trimmedSearchQuery);
		return nameMatch || ownerMatch || descriptionMatch;
	});
};

/**
 * Actions for filtering snippets.
 *
 * Use getFilteredSnippets() to get snippets filtered by the current category and search query.
 * The filtered list is computed on-demand to avoid stale data.
 */
export const filterActions = {
	/**
	 * Action to get filtered snippets based on current filter state.
	 *
	 * The filtered list is computed on every call using the current state.
	 * This is intentional - computing on-demand is simpler and avoids
	 * synchronization issues between filter state and filtered results.
	 *
	 * @param currentUserId - The current user's account ID, used for MY_TEMPLATES filter
	 * @returns Array of snippets filtered by the current category and search query
	 */
	getFilteredSnippets:
		(currentUserId?: string | null) =>
		({ getState }: StoreActionApi<SnippetsState>): Snippet[] => {
			const state = getState();
			return filterSnippets(
				state.snippets,
				state.selectedCategoryId,
				state.searchQuery,
				currentUserId,
			);
		},
	setCategoryFilter:
		(categoryId: SNIPPET_CATEGORY_IDS) =>
		({ setState }: StoreActionApi<SnippetsState>) => {
			setState({ selectedCategoryId: categoryId });
		},
	setSearchQuery:
		(searchQuery: string) =>
		({ setState }: StoreActionApi<SnippetsState>) => {
			setState({ searchQuery });
		},
	resetFilters:
		() =>
		({ setState }: StoreActionApi<SnippetsState>) => {
			setState({
				selectedCategoryId: DEFAULT_CATEGORY_ID,
				searchQuery: '',
			});
		},
};
